import { Box, Col } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IProcess } from '../../../common/interfaces/IProcess';
import type { IRequest } from '../../../common/interfaces/IRequest';
import type { IMaybeLoggedInReduxState, IReduxState } from '../../configureStore';
import { PatientDataError } from '../../../common/enums/PatientDataError';
import { ServiceCode } from '../../../common/enums/ServiceCode';
import { fetchJson } from '../../store/fetch';
import { Container } from '../elements/molecules/container/Container';
import { Content } from '../elements/molecules/content/Content';
import { Main } from '../elements/molecules/main/Main';
import { ConsentForPatientSatisfactionQuestionnaire } from '../elements/organisms/consentForPatientSatisfactionQuestionnaire/ConsentForPatientSatisfactionQuestionnaire';
import { Footer } from '../elements/organisms/footer/Footer';
import { Header } from '../elements/organisms/header/Header';
import { GhcInformationCard } from '../elements/organisms/information/ghc';
import { KsyosSleepInformation } from '../elements/organisms/information/KsyosSleepInformation';
import { OsasInvoicingInformationCard } from '../elements/organisms/osasCards/OsasInvoicingInformationCard';
import { PageWrapper } from '../elements/organisms/pageWrapper/PageWrapper';
import { NewWelcome } from '../elements/organisms/welcome/NewWelcome';
import { Welcome } from '../elements/organisms/welcome/Welcome';
import { YourDataSegment } from '../elements/organisms/yourDataSegment/YourDataSegment';

type HomeProps = {
    error?: PatientDataError;
};

type ProcessOrRequest = {
    serviceCode: ServiceCode;
    startedDateTime: string | null;
};

export function Home(props: Readonly<HomeProps>) {
    const processes = useSelector((state: IReduxState) => state.authentication.processes);
    const requestProcesses = useSelector((state: IReduxState) => state.authentication.requestProcesses);
    const user = useSelector((state: IMaybeLoggedInReduxState) => state.authentication.user);
    const [isLatestCarePathOptometry, setIsLatestCarePathOptometry] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (processes && requestProcesses) {
            checkIsLatestCarePathOptometry();
        }
    }, [processes, requestProcesses, user]);

    // Prevent rendering the page before the optometry check is done
    // Users without an id will also have undefined `processes` and `requestProcesses`
    if (
        user === undefined ||
        (user.id && (!processes || !requestProcesses || isLatestCarePathOptometry === undefined))
    ) {
        return null;
    }

    return (
        <PageWrapper>
            <Header hasLogoutButton />
            <Main>{isLatestCarePathOptometry ? <NewWelcome /> : <LegacyContent error={props.error} />}</Main>
            <Footer />
        </PageWrapper>
    );

    function checkIsLatestCarePathOptometry() {
        const processesAndRequests: ProcessOrRequest[] = [];

        for (const process of processes ?? []) {
            processesAndRequests.push({
                serviceCode: process.service.code,
                startedDateTime: process.startedDatetime,
            });
        }

        for (const request of requestProcesses ?? []) {
            processesAndRequests.push({
                serviceCode: request.service.code,
                startedDateTime: request.dateSent,
            });
        }

        const latestProcess = processesAndRequests.length
            ? orderBy(processesAndRequests, ['startedDateTime'], ['desc'])[0]
            : undefined;

        if (latestProcess?.serviceCode === ServiceCode.TOO || latestProcess?.serviceCode === ServiceCode.TOC) {
            setIsLatestCarePathOptometry(true);
        } else {
            setIsLatestCarePathOptometry(false);
        }
    }
}

function LegacyContent({ error }: Readonly<HomeProps>) {
    const patientId = useSelector((state: IReduxState) => state.authentication.id);
    const requestProcesses = useSelector((state: IReduxState) => state.authentication.requestProcesses);
    const processes = useSelector((state: IReduxState) => state.authentication.processes);
    const [isOsasConsultPerformed, setIsOsasConsultPerformed] = useState(false);

    const hasActiveHomeConsult = requestProcesses?.some(
        (request: IRequest) =>
            [ServiceCode.DermaOth, ServiceCode.GTO].includes(request.service.code) && request.status === 'active'
    );

    const isOsasFlowStarted = Boolean(
        processes?.find((process: IProcess) => process.service.code === ServiceCode.OSAS)
    );

    useEffect(() => {
        getIsOsasConsultPerformed();
    }, [processes]);

    return (
        <Container isFluidOnMobile>
            <Welcome error={error} />

            <Content>
                <Box display={['block', null, 'flex']}>
                    <Box>
                        <KsyosSleepInformation isOsasConsultPerformed={isOsasConsultPerformed} />
                        {hasActiveHomeConsult && <GhcInformationCard />}
                    </Box>

                    {patientId && error !== PatientDataError.generalError && (
                        <Col span={[12, null, 6]}>
                            {isOsasFlowStarted && <OsasInvoicingInformationCard />}
                            <YourDataSegment />

                            {isOsasConsultPerformed && <ConsentForPatientSatisfactionQuestionnaire />}
                        </Col>
                    )}
                </Box>
            </Content>
        </Container>
    );

    async function getIsOsasConsultPerformed() {
        if (!processes) {
            return;
        }
        if (!processes.find((process) => process.service.code === ServiceCode.OSAS)) {
            return;
        }

        const result = await fetchJson(`/api/patient/osas/isOsasConsultPerformed`, 'GET');

        if (result.json) {
            setIsOsasConsultPerformed(result.json.isOsasConsultPerformed);
        }
    }
}
